import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * Reference lists (api-reference-list-service) — the platform's REFLD code
 * tables, e.g.
 *   { MEDICINE_FORMULATIONS: [{ label: "Tablet", value: "REFLD137" }], ... }
 *
 * One endpoint returns EVERY list, so it is fetched once per page load and
 * memoised at module scope: the tables are static master data, and Create Rx
 * would otherwise refetch ~200KB on every visit.
 *
 * Concurrent callers share the in-flight request rather than each firing their
 * own — the medicines mapping and (later) any other consumer both land on the
 * same response.
 */

/** The resolved map, once we have it. */
let cache = null;
/** Callers waiting on the first request: [{ onResponse, onError }]. */
let waiting = null;

class ReferenceListService {
  constructor() {
    this.apiService = new APIService();
  }

  /**
   * `onResponse(tables)` with the whole `{ LIST_NAME: [{label,value}] }` map.
   * A failure is NOT cached — the next caller retries.
   */
  getAll(onResponse, onError) {
    if (cache) {
      onResponse(cache);
      return undefined;
    }
    if (waiting) {
      waiting.push({ onResponse, onError });
      return undefined;
    }
    waiting = [{ onResponse, onError }];

    const flush = (key, arg) => {
      const queued = waiting || [];
      waiting = null;
      queued.forEach((w) => {
        const cb = w[key];
        if (cb) cb(arg);
      });
    };

    return this.apiService.get(
      APIConfig.referenceList.getAllDetails,
      (res) => {
        // The service answers with the bare map; tolerate a `{data: …}` wrapper.
        const tables = (res && res.data) || res || {};
        cache = tables;
        flush("onResponse", tables);
      },
      (err) => flush("onError", err)
    );
  }
}

export default ReferenceListService;
